import React, { useEffect, useMemo } from 'react';

import { useNumberInput, useRequiresPlateType } from '../../../hooks';
import { Event } from '../../../interfaces';
import { useLicensePlateTypes } from '../../../queries';
import { isSentToProcessor } from '../../../utils';
import { Autocomplete } from '../Autocomplete';
import { Regions } from '../../../constants';

interface Props {
  event: Event;
  licensePlateUpdate?: (eventPartial: Partial<Event>) => void;
}

interface NoSaveProps {
  event: Event;
  license_plate_region?: Event['license_plate_region'];
  value?: string | number;
  onChange?: (eventPartial: Partial<Event>) => void;
  required?: boolean;
}
const illinoisCode = Regions[0].states.find((s) => s.state === 'Illinois')?.region;

export const LicensePlateTypeDropdownWithNoSave = (props: NoSaveProps) => {
  const { event, value, required } = props;
  const license_plate_region = props.license_plate_region ?? event?.license_plate_region;

  const fleetRequiresPlateType = useRequiresPlateType();
  const { bind: plateTypeId } = useNumberInput(value ?? '', 'license_plate_type_id');
  const query = useLicensePlateTypes(fleetRequiresPlateType);
  const isIllinois = license_plate_region === illinoisCode;

  const licensePlateTypes = query.data || [];

  const options = useMemo(() => {
    const copy = [...licensePlateTypes];

    // set the top 3 in common order:
    // PAS, TRK,TRT, TXI, PFR, HCP, and then sort the rest alphabetically
    copy.sort((a, b) => {
      const order = ['PAS', 'TRK', 'TRT', 'TXI', 'PFR', 'HCP'];
      const aIndex = order.indexOf(a.value);
      const bIndex = order.indexOf(b.value);

      // If both are in the special order, sort by their position in the order array
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }

      // sort the rest alphabetically
      return a.value?.localeCompare(b.value);
    });

    if (licensePlateTypes[0]?.value) {
      copy.unshift({
        id: '',
        value: licensePlateTypes[0]?.value,
        description: `${licensePlateTypes[0]?.description} (default)`,
      });
    }

    const list = copy.map((lpt) => {
      return {
        ...lpt,
        label: `[${lpt?.value}] ${lpt?.description}`,
        value: lpt.license_plate_type_id,
        id: lpt.license_plate_type_id,
      };
    });

    if (!isIllinois) {
      return list.slice(0, 3); // only allow basic types if not Illinois
    }

    return list;
  }, [licensePlateTypes, isIllinois]);

  useEffect(() => {
    plateTypeId.setValue(value ?? '');
  }, [event?.id, license_plate_region, value]);

  const selectedLPTypeName = useMemo(() => {
    const selectedType = options.find((lpt) => lpt.license_plate_type_id === plateTypeId.value);
    return selectedType?.description ?? '';
  }, [plateTypeId.value, licensePlateTypes]);

  if (!fleetRequiresPlateType) {
    return null;
  }

  const disabled = query.isLoading || (event && isSentToProcessor(event));

  const onChange = async (newValue: number) => {
    const newTypeId = newValue || licensePlateTypes[0]?.license_plate_type_id;
    if (!newTypeId) {
      return;
    }

    plateTypeId.setValue(newTypeId);
    const eventParts: Partial<Event> = {
      license_plate_type_id: newTypeId,
    };

    props.onChange?.({ ...eventParts, id: event.id });
  };

  return (
    <Autocomplete
      onSelect={onChange}
      disabled={disabled}
      required={required}
      value={selectedLPTypeName}
      options={options}
      showNoItemsInfo={false}
      allowCustomValue={false}
      name="license_plate_type"
      autoComplete="off"
      overflow
      placeholder={query.isLoading ? 'Loading...' : undefined}
    />
  );
};

export const LicensePlateTypeDropdown = (props: Props) => {
  const { event } = props;
  const license_plate_type_id = event?.license_plate_type_id ?? '';

  const onChange = async (partial) => {
    props.licensePlateUpdate?.(partial);
  };

  return <LicensePlateTypeDropdownWithNoSave {...props} onChange={onChange} value={license_plate_type_id} required />;
};
